import { useEffect, useState } from 'react';
import { calculateMaterials } from '../../shared/calculations/materials';
import { ProjectState } from '../../shared/types';
import { ProposalResult, sendProposal } from '../proposal';
import { Icon } from './Icon';

interface ProposalDialogProps {
  project: ProjectState;
  result: ProposalResult | null;
  building: boolean;
  buildError: string;
  onClose: () => void;
  onRebuild: (client: { name?: string; phone?: string; comment?: string }) => void;
  onNotify: (message: string) => void;
}

const money = (value: number) => `${Math.round(value).toLocaleString('ru-RU')} ₽`;
const number = (value: number, digits = 2) =>
  value.toLocaleString('ru-RU', { minimumFractionDigits: digits, maximumFractionDigits: digits });

export const ProposalDialog = ({
  project,
  result,
  building,
  buildError,
  onClose,
  onRebuild,
  onNotify,
}: ProposalDialogProps) => {
  const materials = calculateMaterials(project);
  const [phone, setPhone] = useState(() => localStorage.getItem('kafel-lab-client-phone') ?? '');
  const [clientName, setClientName] = useState(() => localStorage.getItem('kafel-lab-client-name') ?? '');
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    localStorage.setItem('kafel-lab-client-phone', phone);
  }, [phone]);

  useEffect(() => {
    localStorage.setItem('kafel-lab-client-name', clientName);
  }, [clientName]);

  const send = async () => {
    if (!result?.proposalId) {
      onNotify('КП ещё формируется — подождите пару секунд');
      return;
    }
    setSending(true);
    setStatus('');
    try {
      const response = await sendProposal(
        result.proposalId,
        phone,
        comment.trim() ? comment.trim() : undefined,
      );
      if (response.ok) {
        setStatus(`Отправлено клиенту в WhatsApp (${response.mode === 'whatsapp' ? 'через шлюз заявок' : 'ссылка'})`);
      } else {
        setStatus(response.error ?? 'Отправить не удалось');
        if (response.url) window.open(response.url, '_blank', 'noopener');
      }
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Не удалось отправить КП');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="proposal-overlay" role="dialog" aria-modal="true" aria-label="Коммерческое предложение">
      <div className="proposal-dialog">
        <header className="proposal-head">
          <div>
            <span className="proposal-eyebrow">Коммерческое предложение</span>
            <h2>{project.name}</h2>
          </div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Закрыть"><Icon name="x" /></button>
        </header>

        {building && <p className="proposal-status">Собираем 3D-картинку, раскладку и расчёт…</p>}
        {buildError && (
          <div className="proposal-error">
            <span>{buildError}</span>
            <button
              type="button"
              className="button secondary"
              onClick={() => onRebuild({ name: clientName, phone, comment })}
            >
              Повторить
            </button>
          </div>
        )}

        <div className="proposal-totals">
          <div><small>Площадь облицовки</small><strong>{number(materials.area)} м²</strong></div>
          <div><small>С запасом {number(materials.effectiveReservePercent, 1)}%</small><strong>{number(materials.areaWithReserve)} м²</strong></div>
          <div><small>Плитка</small><strong>{money(materials.tilesTotal)}</strong></div>
          <div><small>Клей и затирка</small><strong>{money(materials.consumablesTotal)}</strong></div>
          <div className="proposal-total"><small>Итого</small><strong>{money(materials.total)}</strong></div>
        </div>

        <div className="proposal-grid">
          <section>
            <h3>Материалы</h3>
            <ul className="proposal-list">
              {materials.tiles.map((line) => (
                <li key={line.category}>
                  <span><strong>{line.name}</strong><small>{line.format} · {line.pieces} шт{line.packs > 0 ? ` (${line.packs} уп)` : ''}</small></span>
                  <span>{money(line.total)}</span>
                </li>
              ))}
            </ul>
          </section>
          <section>
            <h3>Клей и затирка</h3>
            <ul className="proposal-list">
              {materials.consumables.map((line) => (
                <li key={line.kind}>
                  <span>
                    <strong>{line.kind === 'glue' ? 'Клей' : 'Затирка'}</strong>
                    <small>{number(line.kg, 1)} кг · {line.packs} × {line.packSize} кг</small>
                  </span>
                  <span>{money(line.total)}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>

        <section className="proposal-send">
          <h3>Отправить клиенту</h3>
          <div className="proposal-fields">
            <label className="field">
              <span>Имя клиента</span>
              <span className="input-shell"><input value={clientName} onChange={(event) => setClientName(event.target.value)} placeholder="Например, Али" /></span>
            </label>
            <label className="field">
              <span>Телефон клиента</span>
              <span className="input-shell"><input inputMode="tel" value={phone} onChange={(event) => setPhone(event.target.value)} placeholder="+7 900 000-00-00" /></span>
            </label>
          </div>
          <label className="field">
            <span>Сообщение (необязательно)</span>
            <span className="input-shell"><input value={comment} onChange={(event) => setComment(event.target.value)} placeholder="Комментарий к предложению" /></span>
          </label>
          <div className="proposal-actions">
            <button type="button" className="button primary" disabled={sending || !result} onClick={() => void send()}>
              <Icon name="present" /> {sending ? 'Отправляем…' : 'Отправить в WhatsApp'}
            </button>
            <button
              type="button"
              className="button secondary"
              disabled={building}
              onClick={() => onRebuild({ name: clientName, phone, comment })}
            >
              Пересобрать КП
            </button>
            {result?.proposalUrl && (
              <>
                <button
                  type="button"
                  className="button secondary"
                  onClick={() => {
                    void navigator.clipboard?.writeText(result.proposalUrl ?? '');
                    onNotify('Ссылка на КП скопирована');
                  }}
                >
                  Копировать ссылку
                </button>
                <a className="button secondary" href={result.proposalUrl} target="_blank" rel="noreferrer">Открыть PDF</a>
              </>
            )}
          </div>
          {status && <p className="proposal-status">{status}</p>}
        </section>

        <footer className="proposal-foot">
          <span>{materials.surfaces.length} поверхностей · {materials.tiles.length} позиций плитки · PDF с 3D-визуализацией, раскладкой, спецификацией и итогом</span>
        </footer>
      </div>
    </div>
  );
};
